import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Tabs, Tab, Box, Card, CardContent, CardActions, Avatar, IconButton, Grid, Paper, Link, Stack, Chip, Divider, Fade, Zoom, Container,  } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
  Twitter as TwitterIcon,
  Link as LinkIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  Category as CategoryIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material";
import PodcastsIcon from '@mui/icons-material/Podcasts';
import CastIcon from '@mui/icons-material/Cast';

import client from "../../../../API";
import { useQuery } from "react-query";
import InvitedCreators from "./invitingcastingcalls";
import BookingDialog from "./bookingnew";
import TikTokIcon from "../../../../Components/Icons/TikTok";
import { getHeadersFromKeys } from "../../../../Utils/constants";
import TwitchIcon from "../../../../Components/Icons/Twitch";
import useIsDesktop from "../../../../Hooks/useIsDesktop";

const socialIcons = {
  tiktok: <TikTokIcon />,
  instagram: <InstagramIcon />,
  youtube: <YouTubeIcon />,
  x: <TwitterIcon />,
  twitch: <TwitchIcon></TwitchIcon>,
  podcast:<PodcastsIcon></PodcastsIcon>,
  kick: <CastIcon></CastIcon>,
};

const CastingCallDialog = ({ open, onClose, castingCallId }) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [promotionData, setPromotionData] = useState([]);
  const [error, setError] = useState('');
  const isDesktop = useIsDesktop();

  // Fetch the casting call details
  const { data: castingCall, error: queryError, isLoading, refetch } = useQuery(
    ["castingCall", castingCallId],
    () => client.castingCalls.getById(castingCallId),
    {
      enabled: !!castingCallId,
      refetchOnWindowFocus: false
    }
  );

  // Handle error state
  useEffect(() => {
    if (queryError) {
      console.error("Error fetching casting call:", queryError);
      setError(`Failed to load casting call details: ${queryError?.response?.data?.error || queryError.message}`);
    } else {
      setError(null);
    }
  }, [queryError]);

  const handleTabChange = (event, newValue) => setTabValue(newValue);

  const submitBooking = async (bookingData) => {
    try {
      const postData = await client.partnerships.book(bookingData);
    } catch (error) {
      setError(`Failed to submit booking: ${error?.response?.data?.error || error.message}`);
      console.error("Error submitting booking:", error);
    }
    return false;
  };

  const InfoCard = ({ icon, title, value, color = "primary" }) => (
    <Paper 
      elevation={0}
      sx={{ 
        p: 3,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: alpha(theme.palette[color].main, 0.05),
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: `${color}.main`,
          bgcolor: alpha(theme.palette[color].main, 0.08)
        }
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box 
          sx={{ 
            p: 1.5,
            borderRadius: 2,
            bgcolor: `${color}.main`,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {title}
          </Typography>
          <Typography variant="h6" fontWeight={700} color="text.primary" noWrap>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="xl" 
        fullWidth 
        fullScreen={!isDesktop}
        PaperProps={{
          elevation: 0,
          sx: { 
            boxShadow: theme.shadows[24],
            borderRadius: isDesktop ? 3 : 0,
            bgcolor: 'background.default'
          }
        }}
      >
        {/* Modern Header */}
        <Box 
          sx={{ 
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box 
            sx={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `radial-gradient(circle at 80% 20%, ${alpha(theme.palette.secondary.main, 0.3)} 0%, transparent 70%)`,
              pointerEvents: 'none'
            }}
          />
          
          <Box sx={{ position: 'relative', zIndex: 1, p: 3 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" fontWeight={800} sx={{ mb: 0.5 }}>
                  Casting Call Details
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  {castingCall?.platforms?.join(" • ") || "Loading..."}
                </Typography>
              </Box>
              
              <IconButton 
                onClick={onClose}
                sx={{ 
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.2)'
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>
        </Box>

        <DialogContent 
          dividers 
          sx={{ 
            p: 0,
            bgcolor: 'background.default',
            '&.MuiDialogContent-dividers': {
              borderTop: 'none',
              borderBottom: 'none'
            }
          }}
        >
          {/* Modern Tab Navigation */}
          <Paper 
            elevation={0} 
            square
            sx={{ 
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper'
            }}
          >
            <Container maxWidth="xl">
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange} 
                allowScrollButtonsMobile
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    minHeight: 64,
                    px: 3,
                    '&.Mui-selected': {
                      color: 'primary.main'
                    }
                  },
                  '& .MuiTabs-indicator': {
                    height: 3,
                    borderRadius: '3px 3px 0 0'
                  }
                }}
              >
                <Tab label="Overview" />
                <Tab label="Applications" />
                <Tab label="Invited Creators" />
              </Tabs>
            </Container>
          </Paper>

          <Box sx={{ minHeight: '60vh' }}>
            {error && (
              <Container maxWidth="xl" sx={{ py: 3 }}>
                <Paper 
                  sx={{ 
                    p: 3, 
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'error.main',
                    bgcolor: alpha(theme.palette.error.main, 0.05)
                  }}
                >
                  <Typography color="error.main" variant="body1" fontWeight={500}>
                    {error}
                  </Typography>
                </Paper>
              </Container>
            )}

            {/* Overview Tab */}
            {tabValue === 0 && castingCall && (
              <Container maxWidth="xl" sx={{ py: 4 }}>
                <Fade in timeout={300}>
                  <Box>
                    {/* Info Cards Grid */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                      <Grid item xs={12} sm={6} md={3}>
                        <InfoCard 
                          icon={<CategoryIcon />}
                          title="Platforms"
                          value={castingCall.platforms ? castingCall.platforms.join(", ") : "N/A"}
                          color="primary"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <InfoCard 
                          icon={<MoneyIcon />}
                          title="Budget Range"
                          value={`$${castingCall.budget_min || 0} - $${castingCall.budget_max || 0}`}
                          color="success"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <InfoCard 
                          icon={<PersonIcon />}
                          title="Applications"
                          value={castingCall.applicants?.length || 0}
                          color="info"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <InfoCard 
                          icon={<PersonIcon />}
                          title="Invited"
                          value={castingCall.invited_creators?.length || 0}
                          color="warning"
                        />
                      </Grid>
                    </Grid>

                    {/* Description Card */}
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: 4,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        mb: 4
                      }}
                    >
                      <Stack direction="row" alignItems="flex-start" spacing={3}>
                        <Box 
                          sx={{ 
                            p: 2,
                            borderRadius: 2,
                            bgcolor: 'primary.main',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <DescriptionIcon />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                            Brief
                          </Typography>
                          <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'text.secondary' }}>
                            {castingCall.brief || "No brief provided"}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>

                    {/* Promotion Types */}
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: 4,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
                        Promotion Types
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {castingCall?.promotion_types ? 
                          getHeadersFromKeys(castingCall.promotion_types).map((type, index) => (
                            <Chip 
                              key={index} 
                              label={type}
                              color="primary"
                              variant="outlined"
                              sx={{ 
                                fontWeight: 500,
                                borderRadius: 2
                              }}
                            />
                          )) : 
                          <Typography color="text.secondary">No promotion types specified</Typography>
                        }
                      </Box>
                    </Paper>
                  </Box>
                </Fade>
              </Container>
            )}

            {/* Applications Tab */}
            {tabValue === 1 && castingCall && (
              <Container maxWidth="xl" sx={{ py: 4 }}>
                <Fade in timeout={300}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
                      <Typography variant="h5" fontWeight={700}>
                        Applications ({castingCall.applicants?.length || 0})
                      </Typography>
                    </Box>

                    {castingCall.applicants && castingCall.applicants.length ? (
                      <Grid container spacing={3}>
                        {castingCall.applicants.map((applicant, index) => (
                          <Grid item xs={12} sm={6} lg={4} key={applicant.username}>
                            <Zoom in timeout={300 + index * 100}>
                              <Card 
                                sx={{ 
                                  height: '100%',
                                  borderRadius: 3,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    borderColor: 'primary.main',
                                    boxShadow: theme.shadows[8],
                                    transform: 'translateY(-4px)'
                                  }
                                }}
                              >
                                <CardContent sx={{ p: 3 }}>
                                  <Link
                                    color='inherit'
                                    underline="none"
                                    href={`/creators/${applicant.username}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ textDecoration: 'none' }}
                                  >
                                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                                      <Avatar
                                        src={applicant.pfphref}
                                        alt={applicant.username}
                                        sx={{ 
                                          width: 56, 
                                          height: 56,
                                          border: '3px solid',
                                          borderColor: 'primary.main'
                                        }}
                                      >
                                        {applicant.pfphref ? '' : applicant.username[0]?.toUpperCase()}
                                      </Avatar>
                                      <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography 
                                          variant="h6" 
                                          fontWeight={700}
                                          sx={{ 
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                          }}
                                        >
                                          {applicant.username}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          Content Creator
                                        </Typography>
                                      </Box>
                                    </Stack>
                                  </Link>

                                  {/* Social Links */}
                                  <Box sx={{ mb: 3 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                      Social Platforms
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap">
                                      {Object.entries(applicant.links).map(
                                        ([platform, url]) =>
                                          url && (
                                            <IconButton
                                              key={platform}
                                              component="a"
                                              href={url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              aria-label={platform}
                                              size="small"
                                              sx={{ 
                                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                color: 'primary.main',
                                                '&:hover': {
                                                  bgcolor: 'primary.main',
                                                  color: 'white'
                                                }
                                              }}
                                            >
                                              {socialIcons[platform] || <LinkIcon />}
                                            </IconButton>
                                          )
                                      )}
                                    </Stack>
                                  </Box>
                                </CardContent>

                                <Divider />

                                <CardActions sx={{ p: 3, pt: 2 }}>
                                  <Button
                                    variant="contained"
                                    fullWidth
                                    size="large"
                                    onClick={() => {
                                      setSelectedCreator(applicant);
                                      setBookingDialogOpen(true);
                                    }}
                                    sx={{ 
                                      borderRadius: 2,
                                      fontWeight: 600,
                                      py: 1.5
                                    }}
                                  >
                                    Book Partnership
                                  </Button>
                                </CardActions>
                              </Card>
                            </Zoom>
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Paper 
                        sx={{ 
                          p: 6,
                          textAlign: 'center',
                          borderRadius: 3,
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                          No Applications Yet
                        </Typography>
                        <Typography color="text.secondary">
                          Applications will appear here when creators apply to your casting call.
                        </Typography>
                      </Paper>
                    )}
                  </Box>
                </Fade>
              </Container>
            )}

            {/* Invited Creators Tab */}
            {tabValue === 2 && castingCall && (
              <Box sx={{ p: { xs: 2, md: 0 } }}>
                <InvitedCreators
                  invitedCreators={castingCall.invited_creators || []}
                  castingCall={castingCall}
                  refetchData={refetch}
                />
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions 
          sx={{ 
            p: 3,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Button 
            onClick={onClose} 
            variant="outlined"
            size="large"
            sx={{ 
              borderRadius: 2,
              px: 4,
              py: 1.5,
              fontWeight: 600
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Booking Dialog */}
      {castingCall && castingCall.platforms && (
        <BookingDialog
          open={bookingDialogOpen}
          onClose={() => setBookingDialogOpen(false)}
          submitBooking={submitBooking}
          creator={selectedCreator}
          castingCall={castingCall}
        />
      )}
    </>
  );
};

export default CastingCallDialog;